import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { makeGetSelectIntegrationByTeamIdAndTargetServiceId } from 'selectors';
import { fetchIntegrations, fetchIntegrationStoredProcedures } from 'thunk';
import {
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Box,
  Divider,
  Alert,
} from '@mui/material';
import JsonViewer from 'shared/JsonViewer';
import { copyToClipboard, conditionallyRenderElement } from 'utils';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';
import { AppStateType } from 'reducers';
import { useToggle, useMount } from 'react-use';
import useLocalDispatch from 'hooks/useLocalDispatch';
import { useStateEffect } from 'hooks/useStateEffect';
import { StatusChecklist } from 'components/StatusChecklist';
import { integrationsHash } from '@constants';
import SimpleModal from 'components/SimpleModal';
import { useConfigureIntegrationV2Mutation } from 'IntegrationModule/api/queries/useConfigureIntegrationV2Mutation';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import { useNotification } from 'hooks/useNotification';

const Title = styled(Typography)`
  padding: 0;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const SectionTitle = styled(Typography)`
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const SectionDescription = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

const CollapseButton = styled(Button)`
  margin-top: 0.5rem;
`;

enum InterfaceValue {
  Old = 'old',
  New = 'new',
}

export type SettingsProps = {
  targetServiceId: number;
  teamId: number;
};

const fetchIntegrationStoredProceduresStateId = 'fetchIntegrationStoredProceduresStateId';

const Settings = ({ targetServiceId, teamId }: SettingsProps) => {
  const dispatch = useAppDispatch();
  const { dispatchLocal, selectLocalData } = useLocalDispatch();
  const { showError } = useNotification();

  useMount(() => {
    dispatch(
      fetchIntegrations({
        targetServiceId,
        onSuccess: ({ response }) => {
          const integration = response.data[0];

          const config = integrationsHash[integration.targetService];

          if (integration && config.storeProcedureSupported && config.shortName) {
            const integrationName = config.shortName;

            dispatchLocal(fetchIntegrationStoredProcedures, {
              params: {
                targetServiceId,
                targetService: integration.targetService,
                integrationName,
              },
              stateId: fetchIntegrationStoredProceduresStateId,
              formatSuccessResponse: ({ response }) => {
                const array: Array<{ label: any; status: any }> = [];
                const data = response.data;
                for (const key in data) {
                  const value = data[key];
                  array.push({ label: value.name, status: value.isUploaded });
                }
                return array;
              },
              onFailureCallback: ({ error }) => {
                // ✅ FIX: Replace alert() with toast notification
                showError(error?.data?.message || 'Failed to fetch stored procedures');
              },
            });
          }
        },
      }),
    );
  });

  const selectIntegrationByTeamIdAndTargetServiceId = useMemo(
    () => makeGetSelectIntegrationByTeamIdAndTargetServiceId(),
    [],
  );
  const { data: integration, isRequesting } = useAppSelector((state: AppStateType) =>
    selectIntegrationByTeamIdAndTargetServiceId(state, {
      teamId,
      targetServiceId,
    }),
  );

  const {
    data: fetchIntegrationStoredProceduresData,
    isRequesting: isFetchingIntegrationStoredProcedures,
  } = selectLocalData<{ label: string; status: boolean }[]>({
    stateId: fetchIntegrationStoredProceduresStateId,
  });
  const [shouldCollapseAll, setShouldCollapseAll] = useToggle(false);

  const { configureIntegrationV2 } = useConfigureIntegrationV2Mutation(teamId);
  const { openModal } = useDynamicModal();

  const [interfaceValue, setInterfaceValue] = useStateEffect<InterfaceValue>(
    integration?.settings?.configureWithV2Modal ? InterfaceValue.New : InterfaceValue.Old,
  );

  const openConfirmModal = (value: InterfaceValue) => {
    openModal({
      modal: ({ isOpen, toggle }) => (
        <SimpleModal
          open={isOpen}
          onClose={toggle}
          onConfirm={() => {
            configureIntegrationV2({
              targetServiceId,
              configureWithV2Modal: value === InterfaceValue.New,
              serviceName: integration?.targetService || '',
            });
            setInterfaceValue(value);
            toggle();
          }}
          Body={<div>{`Are you sure you want to switch all users to the ${value} interface?`}</div>}
        />
      ),
    });
  };

  const v2Cutoff = integration?.agentDomain?.settings?.useV2ModalAfter;
  const isDisabledByV2Schedule =
    !v2Cutoff || (!!integration?.createdAt && new Date(integration.createdAt) > new Date(v2Cutoff));

  return (
    <Card style={{ height: '100%' }} elevation={0}>
      <CardContent className="integration-settings-content">
        <Title variant="h5">Integration Settings</Title>

        {/* Interface Version Section */}
        <Box sx={{ mb: 3 }}>
          <SectionTitle variant="subtitle1">User Interface Version</SectionTitle>
          <SectionDescription>
            Choose which configuration interface your users will see when setting up this
            integration.
          </SectionDescription>

          {isDisabledByV2Schedule && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {v2Cutoff
                ? 'This integration was provisioned after the new settings UI was released and automatically uses the new interface.'
                : 'Interface version toggle is not supported for this integration type.'}
            </Alert>
          )}

          <Tooltip
            title={
              isDisabledByV2Schedule
                ? v2Cutoff
                  ? 'This integration was provisioned after the new settings UI was released.'
                  : 'Integration V2 Toggle not supported for this integration.'
                : ''
            }
          >
            <span>
              <FormControl
                variant="outlined"
                fullWidth
                disabled={isDisabledByV2Schedule}
                size="medium"
              >
                <InputLabel id="integration-settings-select-label">Interface Type</InputLabel>
                <Select
                  labelId="integration-settings-select-label"
                  value={interfaceValue}
                  onChange={(e) => {
                    const newValue = e.target.value as InterfaceValue;
                    openConfirmModal(newValue);
                  }}
                  label="Interface Type"
                >
                  <MenuItem value={InterfaceValue.Old}>
                    <Box>
                      <Typography variant="body1">Legacy Interface</Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Classic configuration experience
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value={InterfaceValue.New}>
                    <Box>
                      <Typography variant="body1">Modern Interface</Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Enhanced UI with improved workflow
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </span>
          </Tooltip>
        </Box>

        {/* Stored Procedures Section */}
        {conditionallyRenderElement({
          isLoading: !!isFetchingIntegrationStoredProcedures,
          error: undefined,
          content: fetchIntegrationStoredProceduresData ? (
            <Box sx={{ mb: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <StatusChecklist
                title="Stored Procedures"
                items={fetchIntegrationStoredProceduresData}
              />
            </Box>
          ) : (
            <></>
          ),
        })}

        {/* Technical Details Section */}
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              mb: 1,
            }}
          >
            <Box
              sx={{
                flex: 1,
              }}
            >
              <SectionTitle variant="subtitle1">Technical Configuration</SectionTitle>
              <SectionDescription>
                Advanced integration settings and configuration details for developers.
              </SectionDescription>
            </Box>
            <CollapseButton
              onClick={setShouldCollapseAll}
              variant="outlined"
              size="small"
              color="primary"
              sx={{
                whiteSpace: 'nowrap',
                minWidth: 'fit-content',
                flexShrink: 0,
              }}
            >
              {shouldCollapseAll ? 'Expand All' : 'Collapse All'}
            </CollapseButton>
          </Box>

          {conditionallyRenderElement({
            isLoading: isRequesting as boolean,
            error: undefined,
            content: !integration ? (
              <Alert severity="info">
                This integration uses default settings. No custom configuration is applied.
              </Alert>
            ) : (
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'background.default',
                }}
              >
                <JsonViewer
                  collapsed={shouldCollapseAll}
                  src={integration}
                  enableClipboard={copyToClipboard}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  style={{ fontSize: '0.875rem' }}
                />
              </Box>
            ),
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default Settings;
